/**
 * Customer help & support — WhatsApp and phone call shortcuts.
 */
import { MessageCircle, Phone } from 'lucide-react';
import { CONTACT_EMAIL, SUPPORT_WHATSAPP_NUMBER, SUPPORT_WHATSAPP_URL } from '../config';

const WHATSAPP_PREFILL = encodeURIComponent(
  'Hello SmartPay Utilities, I need help with my account or a payment.'
);

function formatDisplayPhone(e164) {
  const digits = String(e164 || '').replace(/\D/g, '');
  if (digits.startsWith('265') && digits.length >= 12) {
    const local = digits.slice(3);
    if (local.length === 9) {
      return `+265 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
    }
  }
  return digits ? `+${digits}` : '';
}

const displayPhone = formatDisplayPhone(SUPPORT_WHATSAPP_NUMBER);
const telHref = `tel:+${SUPPORT_WHATSAPP_NUMBER.replace(/\D/g, '')}`;
const whatsAppHref = `${SUPPORT_WHATSAPP_URL}?text=${WHATSAPP_PREFILL}`;

export default function ContactSupportButtons({ compact = false, className = '' }) {
  if (compact) {
    return (
      <div className={`flex flex-col sm:flex-row gap-2 ${className}`}>
        <a
          href={telHref}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-sm font-medium"
          data-testid="support-call-btn"
        >
          <Phone className="w-4 h-4" />
          Call
        </a>
        <a
          href={whatsAppHref}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-all text-sm font-medium"
          data-testid="support-whatsapp-btn"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp
        </a>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <p className="font-medium text-sm">Need help?</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Contact us for payments, tokens, refunds, and account support.
        </p>
      </div>
      <a
        href={whatsAppHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="support-whatsapp-btn"
        className="flex items-center gap-3 p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/15 transition-all"
      >
        <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <p className="font-medium text-emerald-700 dark:text-emerald-400">Chat on WhatsApp</p>
          <p className="text-sm text-muted-foreground">{displayPhone}</p>
        </div>
      </a>
      <a
        href={telHref}
        data-testid="support-call-btn"
        className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-all"
      >
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
          <Phone className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="font-medium">Call us</p>
          <p className="text-sm text-muted-foreground">{displayPhone}</p>
        </div>
      </a>
      <a
        href={`mailto:${CONTACT_EMAIL}`}
        data-testid="support-email-link-inline"
        className="text-xs text-muted-foreground hover:text-primary transition-colors"
      >
        Or email {CONTACT_EMAIL}
      </a>
    </div>
  );
}

export { displayPhone as supportDisplayPhone, telHref as supportTelHref, whatsAppHref as supportWhatsAppHref };
